// @ts-check
import session from '@flowio/session';
import request from '@flowio/request';

import { SessionCache } from '@flowio/cache';
import config from './config';
import constants from './constants/index';
import { memo } from './utilities/memo';
import optionsSuccess from './utilities/options-success';

const cache = new SessionCache({ storageKey: 'flow_price_facets' });

/**
 * @typedef FacetDetails
 * @property {string} amount
 * @property {string} boundary
 * @property {Element} el
*/

/**
 *
 * @param {object} options
 * @param {object} options.data
 * @returns {Promise<import('@flowio/types/dist/api').ExperiencePriceFacetConversionResponse>}
 */
const fetchLocalizedPriceFacets = memo(({ experience, data }) => new Promise((resolve, reject) => {
  const baseUrl = config.API_BASE_URI;
  const organization = session.getOrganization();
  const url = `${baseUrl}/${organization}/experiences/${experience}/conversions/price/facet`;
  const cachedResult = cache.get(data, experience, organization);

  if (cachedResult) {
    resolve(cachedResult);
  }

  request.anonymous({
    url,
    data,
  },
  /** @type {(
    status: number,
    response: import('@flowio/types/dist/api').ExperiencePriceFacetConversionResponse
  ) => void} */
  (status, response) => {
    if (status === 200) {
      cache.set(data, response, experience, organization);
      resolve(response);
    } else {
      reject(response);
    }
  });
}));

/**
 * @returns {Promise<void>}
 */
export function localize(options = {}) {
  const experience = session.getExperience();
  if (!experience) {
    optionsSuccess(options);
    return Promise.resolve();
  }
  /** @type {Element[]} */
  const priceFacetEls = [].slice.call(
    document.querySelectorAll(`[${constants.PRICE_FACET_AMOUNT_ATTRIBUTE}]`),
  );
  let baseCurrency;
  /** @type {FacetDetails[]} */
  const amountBoundaryData = [];
  priceFacetEls.forEach((el) => {
    const amountAttr = el.getAttribute(constants.PRICE_FACET_AMOUNT_ATTRIBUTE);
    const amount = amountAttr && el.getAttribute(amountAttr);
    const boundary = el.getAttribute(constants.PRICE_FACET_BOUNDARY);
    // base currency is only required once.
    if (!baseCurrency) {
      baseCurrency = el.getAttribute(constants.PRICE_FACET_CURRENCY);
    }

    if (amount && boundary) {
      amountBoundaryData.push({
        amount,
        boundary,
        el,
      });
    }
  });

  if (amountBoundaryData.length > 0) {
    const boundaryParams = amountBoundaryData.reduce((acc, facet) => {
      const boundary = facet.boundary;
      acc[boundary] = acc[boundary] || [];
      acc[boundary].push(facet.amount);
      return acc;
    }, {});

    const data = {
      base: baseCurrency,
      local: session.getGeo().currency,
      ...boundaryParams,
    };


    return fetchLocalizedPriceFacets({ experience, data }).then((
      /** @type {import('@flowio/types/dist/api').ExperiencePriceFacetConversionResponse} */
      conversions,
    ) => {
      conversions.facets.forEach((facet) => {
        const facetBoundary = amountBoundaryData.find(facetDetail => (
          parseFloat(facetDetail.amount) === facet.request.value
          && facetDetail.boundary === facet.request.boundary
        ));
        if (facetBoundary) {
          facetBoundary.el.textContent = facet.price.label;
        }
      });
      optionsSuccess(options);
    },
    (error) => {
      // eslint-disable-next-line no-console
      if (console && console.warn) {
        // eslint-disable-next-line no-console
        console.warn(`[Flow] price facets l10n request failed. ${JSON.stringify(error)}`);
      }
      optionsSuccess(options);
    });
  }
  optionsSuccess(options);
  return Promise.resolve();
}

export default {
  localize,
};



// WEBPACK FOOTER //
// ./static/js/v0/price-facets.js